import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useI18n } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { GoogleLogo, Eye, EyeSlash, ArrowRight, Play, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { getApiErrorMessage } from "@/lib/apiErrors";
import { applyAuthSession } from "@/lib/authSession";
import TwoFAPrompt from "@/components/TwoFAPrompt";
import { toast } from "sonner";
function FmarkIcon({ className = "h-7 w-auto", fill = "white" }) {
  return (
    <svg aria-hidden="true" className={`${className} shrink-0`} viewBox="8 3 87 93" fill={fill} xmlns="http://www.w3.org/2000/svg">
      <path d="M21.205,29.001h16.003h16.745h11.526c6.966,0,12.629-5.754,12.629-12.824c0-7.072-5.663-12.826-12.629-12.826H53.679H36.936h-15.73c-6.968,0-12.634,5.754-12.634,12.826C8.571,23.247,14.237,29.001,21.205,29.001z" />
      <path d="M82.22,36.832H21.205c-6.968,0-12.634,5.761-12.634,12.831c0,7.073,5.666,12.824,12.634,12.824H82.22c6.968,0,12.634-5.751,12.634-12.824C94.854,42.593,89.188,36.832,82.22,36.832z" />
      <path d="M53.682,70.511c0-0.055-0.003-0.11-0.003-0.162h-1.901H23.236h-2.031c-6.968,0-12.634,5.754-12.634,12.823C8.571,90.246,14.237,96,21.205,96h2.303h28.27h2.176c0-0.069,0-0.137,0-0.203c5.931-1.049,10.458-6.307,10.458-12.625C64.411,76.76,59.746,71.448,53.682,70.511z" />
    </svg>
  );
}

const GOOGLE_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim();

function _loadGoogleGIS() {
  return new Promise((resolve) => {
    if (window.google?.accounts?.oauth2) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = resolve;
    document.head.appendChild(s);
  });
}

export default function Login() {
  const { t, lang, setLang } = useI18n();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const googleConfigured = Boolean(GOOGLE_CLIENT_ID);

  // mode: "login" | "register" | "magic"
  const [mode, setMode] = useState(() => (location.pathname === "/register" ? "register" : "login"));
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // login fields
  const [email, setEmail] = useState(() => localStorage.getItem("factlify_last_email") || "");
  const [password, setPassword] = useState("");

  // register extras
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  // magic link
  const [magicSent, setMagicSent] = useState(false);

  // 2FA challenge (returned by login endpoints when TOTP is enabled)
  const [challengeToken, setChallengeToken] = useState("");

  // demo form
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [demoFields, setDemoFields] = useState({ name: "", email: "", company: "", phone: "" });
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState("");

  useEffect(() => {
    const expired = sessionStorage.getItem("factlify_session_expired");
    if (expired) {
      sessionStorage.removeItem("factlify_session_expired");
      api.post("/auth/logout").catch(() => {});
      toast.info(lang === "es" ? "Tu sesión ha expirado. Inicia sesión de nuevo." : "Your session has expired. Please sign in again.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleLogin = async () => {
    if (!googleConfigured) return;
    setError("");
    setLoading(true);
    try {
      await _loadGoogleGIS();
      const accessToken = await new Promise((resolve, reject) => {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: "openid email profile",
          callback: (resp) => {
            if (resp.error) reject(new Error(resp.error));
            else resolve(resp.access_token);
          },
        });
        client.requestAccessToken({ prompt: "select_account" });
      });
      const { data } = await api.post("/auth/google-login", { access_token: accessToken });
      afterLogin(data);
    } catch (err) {
      setError(getApiErrorMessage(err, "No se pudo iniciar sesión con Google."));
      setLoading(false);
    }
  };

  const afterLogin = (data) => {
    if (data?.requires_2fa && data?.challenge_token) {
      setChallengeToken(data.challenge_token);
      setLoading(false);
      return;
    }
    applyAuthSession(data);
    setUser(data.user);
    navigate("/dashboard", { replace: true });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) { setError("Rellena todos los campos."); return; }
    if (mode === "register" && !name.trim()) { setError("El nombre es obligatorio."); return; }
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/password-login";
      const payload = mode === "register"
        ? { email: email.trim().toLowerCase(), password, name: name.trim(), phone: phone.trim() }
        : { email: email.trim().toLowerCase(), password };
      const { data } = await api.post(endpoint, payload);
      localStorage.setItem("factlify_last_email", email.trim().toLowerCase());
      afterLogin(data);
    } catch (err) {
      setError(getApiErrorMessage(err, "Error al iniciar sesión. Inténtalo de nuevo."));
    } finally {
      setLoading(false);
    }
  };

  const handleMagicRequest = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) { setError("Introduce un email válido."); return; }
    setLoading(true);
    try {
      await api.post("/auth/magic-request", { email: trimmed });
      localStorage.setItem("factlify_last_email", trimmed);
      setMagicSent(true);
    } catch (err) {
      setError(getApiErrorMessage(err, "No se pudo enviar el email. Inténtalo de nuevo."));
    } finally {
      setLoading(false);
    }
  };

  const handleDemo = async (e) => {
    e.preventDefault();
    setDemoError("");
    const { name: n, email: em, company, phone: ph } = demoFields;
    if (!n.trim()) { setDemoError("Introduce tu nombre."); return; }
    if (!em.trim() || !em.includes("@")) { setDemoError("Introduce un email válido."); return; }
    if (!company.trim()) { setDemoError("Introduce el nombre de tu empresa."); return; }
    setDemoLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const { data } = await api.post("/auth/demo",
        { name: n.trim(), email: em.trim().toLowerCase(), company: company.trim(), phone: ph.trim() },
        { signal: controller.signal },
      );
      afterLogin(data);
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") {
        setDemoError("El servidor tardó demasiado. Inténtalo de nuevo.");
      } else {
        setDemoError(getApiErrorMessage(err, "No se pudo iniciar la demo. Inténtalo de nuevo."));
      }
    } finally {
      clearTimeout(timer);
      setDemoLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
    setPassword("");
    setMagicSent(false);
  };

  return (
    <div className="min-h-screen bg-ink text-white flex flex-col">
      <div className="h-1.5 bg-safety w-full" aria-hidden="true" data-testid="brand-stripe" />
      <div className="flex-1 grid lg:grid-cols-2 min-h-0">
      {/* Panel izquierdo (solo desktop) */}
      <div className="relative hidden lg:block overflow-hidden border-r-2 border-ink">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,#1d4ed8_0%,#0f172a_45%,#09090b_100%)]" />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <div>
            <div className="overline text-white/70 mb-6">SYSTEM · BUILD 01</div>
            <h1 className="font-display font-black text-5xl tracking-tight leading-[0.95]">
              {lang === "es" ? <>La gestión que <span className="text-safety">entiende</span><br/>tu oficio.</>
                : lang === "ca" ? <>La gestió que <span className="text-safety">entén</span><br/>el teu ofici.</>
                : <>Management that <span className="text-safety">understands</span><br/>your trade.</>}
            </h1>
            <p className="mt-6 max-w-md font-sans text-white/80 text-sm leading-relaxed">
              {lang === "es"
                ? "Clientes, presupuestos, facturas, cobros y gastos en un único lugar. Hecho para autónomos y pymes, desde el móvil."
                : lang === "ca"
                  ? "Clients, pressupostos, factures, cobraments i despeses en un sol lloc. Fet per a autònoms i pimes, des del mòbil."
                  : "Clients, quotes, invoices, payments and expenses in one place. Built for freelancers and small businesses, from your phone."}
            </p>
          </div>
          <div className="grid grid-cols-4 gap-3 border-t-2 border-white/20 pt-6">
            {[
              { code: "REF", label: lang === "es" ? "Reformas" : lang === "ca" ? "Reformes" : "Renovations" },
              { code: "FON", label: lang === "es" ? "Fontanería" : lang === "ca" ? "Lampisteria" : "Plumbing" },
              { code: "ELE", label: lang === "es" ? "Electricidad" : lang === "ca" ? "Electricitat" : "Electrical" },
              { code: "PIN", label: lang === "es" ? "Pintura" : lang === "ca" ? "Pintura" : "Painting" },
            ].map((s) => (
              <div key={s.code}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{s.code}</div>
                <div className="font-display font-bold text-base">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Panel derecho */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">

          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2.5">
              <FmarkIcon className="h-7 w-auto" fill="white" />
              <span className="font-display font-black text-2xl tracking-tight leading-none text-safety">FACTLIFY</span>
            </div>
            <div className="flex border-2 border-white text-xs font-bold">
              {["es", "en", "ca"].map((l) => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
                  className={`px-3 py-1.5 uppercase tracking-widest ${lang === l ? "bg-white text-ink" : "bg-transparent text-white/80 hover:bg-white/10"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {challengeToken ? (
            <TwoFAPrompt challengeToken={challengeToken} onSuccess={afterLogin} />
          ) : (
          <div className="border-2 border-white/20 bg-white/5 p-8">

            {/* Título */}
            <h2 className="font-display font-black text-3xl tracking-tight mb-1">
              {mode === "register"
                ? (lang === "es" ? "Crear cuenta" : lang === "ca" ? "Crear compte" : "Create account")
                : (lang === "es" ? "Bienvenido" : lang === "ca" ? "Benvingut" : "Welcome back")}
            </h2>
            <p className="text-sm text-white/70 mb-6">
              {lang === "es"
                ? "Prueba gratis sin restricciones durante 14 días"
                : lang === "ca"
                  ? "Prova gratuïta sense restriccions durant 14 dies"
                  : "Free trial, no restrictions, 14 days"}
            </p>

            {/* Botón Google */}
            {googleConfigured && (
              <>
                <button
                  data-testid="google-login-btn"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-3 font-bold uppercase tracking-wider px-5 py-3.5 border-2 bg-ink text-white border-ink hover:bg-blueprint hover:border-blueprint active:translate-y-0.5 transition-all min-h-[48px]"
                >
                  <GoogleLogo size={18} weight="bold" />
                  {lang === "es" ? "Continuar con Google" : lang === "ca" ? "Continua amb Google" : "Continue with Google"}
                </button>
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 border-t border-white/15" />
                  <span className="text-[10px] uppercase tracking-widest text-white/50 font-mono">
                    {lang === "es" ? "o con tu email" : lang === "ca" ? "o amb el teu email" : "or with email"}
                  </span>
                  <div className="flex-1 border-t border-white/15" />
                </div>
              </>
            )}

            {/* Formulario magic-link (modo "magic") */}
            {mode === "magic" ? (
              magicSent ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 border-2 border-green-500 bg-green-50">
                    <CheckCircle size={22} weight="fill" className="text-green-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-sm text-green-800">Revisa tu email</p>
                      <p className="text-xs text-green-700 mt-1">
                        Enlace enviado a <strong>{email}</strong>. Caduca en 15 min.
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => { setMagicSent(false); setMode("login"); }}
                    className="text-xs text-steel underline">
                    ← Volver al inicio de sesión
                  </button>
                </div>
              ) : (
                <form onSubmit={handleMagicRequest} className="space-y-4">
                  <p className="text-sm text-steel">
                    {lang === "es" ? "Te enviamos un enlace de acceso por email. Sin contraseña." : "We'll send you a login link by email. No password needed."}
                  </p>
                  <div>
                    <label htmlFor="magic-email-input" className="block text-[10px] font-bold uppercase tracking-wider mb-1.5">Email</label>
                    <input
                      id="magic-email-input"
                      data-testid="magic-login-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@empresa.com"
                      required
                      autoFocus
                      autoComplete="email"
                      className="w-full border-2 border-white bg-transparent text-white placeholder-white/40 px-3.5 py-3 min-h-[48px] text-base focus:border-safety outline-none"
                    />
                  </div>
                  {error && <p role="alert" className="text-xs text-red-600 font-mono">{error}</p>}
                  <button data-testid="magic-login-btn" type="submit" disabled={loading}
                    className="w-full flex items-center justify-center gap-2 font-bold uppercase tracking-wider px-5 py-3.5 border-2 bg-ink text-white border-ink hover:bg-blueprint hover:border-blueprint transition-all disabled:opacity-50 min-h-[48px]">
                    <PaperPlaneTilt size={18} weight="bold" />
                    {loading ? "Enviando…" : "Enviar enlace de acceso"}
                  </button>
                  <button type="button" onClick={() => switchMode("login")}
                    className="text-xs text-steel underline w-full text-center">
                    ← Volver al inicio de sesión
                  </button>
                </form>
              )
            ) : (
              /* Formulario email + contraseña */
              <form onSubmit={handlePasswordSubmit} className="space-y-4">

                {/* Nombre (solo registro) */}
                {mode === "register" && (
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5">
                      {lang === "es" ? "Nombre y apellidos" : lang === "ca" ? "Nom i cognoms" : "Full name"} *
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Juan García"
                      required
                      autoFocus
                      autoComplete="name"
                      className="w-full border-2 border-white bg-transparent text-white placeholder-white/40 px-3.5 py-3 min-h-[48px] text-base focus:border-safety outline-none"
                    />
                  </div>
                )}

                {/* Email */}
                <div>
                  <label htmlFor="login-email-input" className="block text-[10px] font-bold uppercase tracking-wider mb-1.5">
                    {lang === "es" ? "Correo electrónico" : lang === "ca" ? "Correu electrònic" : "Email"}
                  </label>
                  <input
                    id="login-email-input"
                    data-testid="magic-login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@empresa.com"
                    required
                    autoFocus={mode === "login" && !email}
                    autoComplete="email"
                      className="w-full border-2 border-white bg-transparent text-white placeholder-white/40 px-3.5 py-3 min-h-[48px] text-base focus:border-safety outline-none"
                  />
                </div>

                {/* Contraseña */}
                <div>
                  <label htmlFor="login-password-input" className="block text-[10px] font-bold uppercase tracking-wider mb-1.5">
                    {lang === "es" ? "Contraseña" : lang === "ca" ? "Contrasenya" : "Password"}
                  </label>
                  <div className="relative">
                    <input
                      id="login-password-input"
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={mode === "register" ? (lang === "es" ? "Mínimo 8 caracteres" : "At least 8 characters") : "••••••••"}
                      required
                      autoComplete={mode === "register" ? "new-password" : "current-password"}
                      className="w-full border-2 border-white bg-transparent text-white placeholder-white/40 px-3.5 py-3 pr-11 min-h-[48px] text-base focus:border-safety outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass((v) => !v)}
                      aria-label={showPass ? (lang === "es" ? "Ocultar contraseña" : "Hide password") : (lang === "es" ? "Mostrar contraseña" : "Show password")}
                      aria-pressed={showPass}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-steel hover:text-ink p-1"
                      tabIndex={-1}
                    >
                      {showPass ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Teléfono (solo registro) */}
                {mode === "register" && (
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5">
                      {lang === "es" ? "Teléfono" : lang === "ca" ? "Telèfon" : "Phone"}
                    </label>
                    <div className="flex gap-2">
                      <div className="flex items-center gap-1 border-2 border-ink px-3 py-3 bg-white text-sm font-mono min-h-[48px] shrink-0">
                        🇪🇸 +34
                      </div>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="600 000 000"
                        autoComplete="tel"
                        className="flex-1 bg-white border-2 border-ink px-3 py-3 focus:border-blueprint focus:outline-none min-h-[48px]"
                      />
                    </div>
                  </div>
                )}

                {error && <p role="alert" className="text-xs text-red-600 font-mono">{error}</p>}

                {/* Botón principal */}
                <button
                  data-testid="magic-login-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 font-bold uppercase tracking-wider px-5 py-3.5 border-2 bg-safety text-white border-safety hover:bg-safety/90 active:translate-y-0.5 transition-all disabled:opacity-50 min-h-[48px]"
                >
                  <ArrowRight size={18} weight="bold" />
                  {loading
                    ? (lang === "es" ? "Entrando…" : "Signing in…")
                    : mode === "register"
                      ? (lang === "es" ? "Registrarse" : lang === "ca" ? "Registrar-se" : "Create account")
                      : (lang === "es" ? "Iniciar sesión" : lang === "ca" ? "Iniciar sessió" : "Sign in")}
                </button>

                {/* Toggle login/registro */}
                <div className="text-center text-sm pt-1">
                  {mode === "login" ? (
                    <>
                      <span className="text-white/70">{lang === "es" ? "¿No tienes cuenta?" : "Don't have an account?"}</span>{" "}
                      <button type="button" onClick={() => switchMode("register")}
                        className="font-bold text-blueprint hover:underline">
                        {lang === "es" ? "Crear cuenta gratis" : lang === "ca" ? "Crear compte gratuït" : "Create free account"}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-white/70">{lang === "es" ? "¿Ya tienes cuenta?" : "Already have an account?"}</span>{" "}
                      <button type="button" onClick={() => switchMode("login")}
                        className="font-bold text-blueprint hover:underline">
                        {lang === "es" ? "Iniciar sesión" : lang === "ca" ? "Iniciar sessió" : "Sign in"}
                      </button>
                    </>
                  )}
                </div>

                {/* Magic link como fallback */}
                {mode === "login" && (
                  <div className="text-center">
                    <button type="button" onClick={() => switchMode("magic")}
                      className="text-[11px] text-white/70 hover:text-white underline">
                      {lang === "es" ? "¿Olvidaste tu contraseña? Recibe un enlace de acceso" : "Forgot password? Get a login link"}
                    </button>
                  </div>
                )}
              </form>
            )}

            {/* Demo */}
            <div className="mt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 border-t border-white/15" />
                <span className="text-[10px] uppercase tracking-widest text-white/50 font-mono">
                  {lang === "es" ? "o prueba gratis 14 días" : lang === "ca" ? "o prova gratis 14 dies" : "or try free 14 days"}
                </span>
                <div className="flex-1 border-t border-white/15" />
              </div>

              {!showDemoForm ? (
                <>
                  <button type="button" onClick={() => setShowDemoForm(true)} data-testid="demo-btn"
                    className="w-full flex items-center justify-center gap-2 font-bold uppercase tracking-wider px-5 py-3 border-2 border-blueprint text-blueprint hover:bg-blueprint hover:text-white active:translate-y-0.5 transition-all text-sm min-h-[44px]">
                    <Play size={16} weight="bold" />
                    {lang === "es" ? "Probar demo gratis" : lang === "ca" ? "Provar demo gratis" : "Try free demo"}
                  </button>
                  <p className="text-[10px] text-white/60 text-center mt-1.5">
                    {lang === "es" ? "Datos de ejemplo precargados · Sin tarjeta · 14 días" : "Sample data preloaded · No card · 14 days"}
                  </p>
                </>
              ) : (
                <form onSubmit={handleDemo} className="space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="demo-name" className="block text-[10px] font-bold uppercase tracking-wider mb-1">{lang === "es" ? "Nombre" : "Name"} *</label>
                      <input id="demo-name" type="text" value={demoFields.name} onChange={(e) => setDemoFields((p) => ({ ...p, name: e.target.value }))}
                        placeholder={lang === "es" ? "Tu nombre" : "Your name"} autoFocus autoComplete="name"
                        className="w-full border-2 border-white bg-transparent text-white placeholder-white/40 px-3 py-2 text-sm focus:border-safety focus:outline-none" />
                    </div>
                    <div>
                      <label htmlFor="demo-company" className="block text-[10px] font-bold uppercase tracking-wider mb-1">{lang === "es" ? "Empresa" : "Company"} *</label>
                      <input id="demo-company" type="text" value={demoFields.company} onChange={(e) => setDemoFields((p) => ({ ...p, company: e.target.value }))}
                        placeholder={lang === "es" ? "Tu empresa" : "Your company"} autoComplete="organization"
                        className="w-full border-2 border-white bg-transparent text-white placeholder-white/40 px-3 py-2 text-sm focus:border-safety focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="demo-email" className="block text-[10px] font-bold uppercase tracking-wider mb-1">Email *</label>
                    <input id="demo-email" type="email" value={demoFields.email} onChange={(e) => setDemoFields((p) => ({ ...p, email: e.target.value }))}
                      placeholder="tu@empresa.com" autoComplete="email"
                      className="w-full border-2 border-white bg-transparent text-white placeholder-white/40 px-3 py-2 text-sm focus:border-safety focus:outline-none" />
                  </div>
                  <div>
                    <label htmlFor="demo-phone" className="block text-[10px] font-bold uppercase tracking-wider mb-1">{lang === "es" ? "Teléfono" : "Phone"}</label>
                    <input id="demo-phone" type="tel" value={demoFields.phone} onChange={(e) => setDemoFields((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="600 000 000" autoComplete="tel"
                      className="w-full border-2 border-white bg-transparent text-white placeholder-white/40 px-3 py-2 text-sm focus:border-safety focus:outline-none" />
                  </div>
                  {demoError && <p role="alert" className="text-xs text-red-600 font-mono">{demoError}</p>}
                  <button type="submit" disabled={demoLoading} data-testid="demo-submit-btn"
                    className="w-full flex items-center justify-center gap-2 font-bold uppercase tracking-wider px-5 py-3 border-2 bg-blueprint text-white border-blueprint hover:bg-ink hover:border-ink transition-all disabled:opacity-50 text-sm min-h-[44px]">
                    <ArrowRight size={16} weight="bold" />
                    {demoLoading ? (lang === "es" ? "Preparando demo…" : "Setting up demo…") : (lang === "es" ? "Empezar demo gratis" : "Start free demo")}
                  </button>
                  <button type="button" onClick={() => { setShowDemoForm(false); setDemoError(""); }}
                    className="text-[10px] text-white/60 underline w-full text-center">
                    {lang === "es" ? "Cancelar" : "Cancel"}
                  </button>
                </form>
              )}
            </div>

            <div className="mt-5 border-t-2 border-slate-200 pt-4 text-[11px] text-steel font-mono uppercase tracking-widest">
              {t.login.footer}
            </div>
          </div>
          )}

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] font-mono uppercase tracking-widest text-steel">
            {[
              lang === "es" ? "PRESUPUESTOS" : lang === "ca" ? "PRESSUPOSTOS" : "QUOTES",
              lang === "es" ? "FACTURAS" : lang === "ca" ? "FACTURES" : "INVOICES",
              lang === "es" ? "GASTOS" : lang === "ca" ? "DESPESES" : "EXPENSES",
              "VERIFACTU",
            ].map((x) => (
              <div key={x} className="border-2 border-slate-300 px-2 py-1.5 text-center bg-white">{x}</div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
